import urls from "global/constants/UrlConstants";
import { getCallParams } from "utils/service";
import { ContractBatchStatus } from "./ContractSearchTypes";

/**
 * A live connection to one ingest batch's progress. The AI pushes a full snapshot whenever anything
 * in the batch changes, and core relays that stream because the AI needs a service token the browser
 * must never hold.
 *
 * Written on `fetch` rather than `EventSource`, which cannot set headers -- and our session is a
 * bearer token. A small reader rather than a library, so the app takes no new dependency.
 */

/** Wait before reconnecting after a drop. The AI caps a connection at 30 minutes by design. */
const RECONNECT_MS = 3000;
/** Stop reconnecting eventually, so a broken batch cannot reconnect for ever in a background tab. */
const MAX_RECONNECTS = 20;

interface StreamHandlers {
  /** A full snapshot; called for every `progress` event, including the first on connect. */
  onProgress: (status: ContractBatchStatus) => void;
  /** Every document is embedded or failed. Terminal -- the stream will not reconnect. */
  onDone: () => void;
  /** The stream gave up. The caller decides what to show; it is never retried after this. */
  onFailed: () => void;
}

/** One `event:`/`data:` block from the wire. */
const parseEvent = (block: string): { event: string; data: string } | null => {
  let event = "message";
  const data: string[] = [];
  for (const line of block.split("\n")) {
    // A line starting with ":" is a comment -- the AI sends ": ping" to hold the connection open.
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trim());
  }
  return data.length ? { event, data: data.join("\n") } : null;
};

/**
 * Opens the stream and keeps it open. Returns a function that closes it; call that on unmount, or
 * the connection outlives the screen.
 */
export const streamContractBatchStatus = (
  batchId: string,
  handlers: StreamHandlers,
): (() => void) => {
  const controller = new AbortController();
  let stopped = false;
  let attempts = 0;
  // Once the stream is open core has already sent its 200, so it cannot answer "no such batch" with
  // a status any more: a connection that opens and closes having said nothing is the signal.
  let everReceived = false;

  const stop = () => {
    stopped = true;
    controller.abort();
  };

  const connect = async (): Promise<void> => {
    const params = await getCallParams("GET");
    const response = await fetch(
      `${urls.CONTRACT_BATCH_STATUS_STREAM}?batchId=${encodeURIComponent(batchId)}`,
      {
        method: "GET",
        headers: { ...params.headers, Accept: "text/event-stream" },
        signal: controller.signal,
      },
    );
    if (!response.ok || !response.body) {
      throw new Error(`status stream responded ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done || stopped) return;
      buffer += decoder.decode(value, { stream: true });

      // Events are separated by a blank line; anything after the last one is a partial event and
      // stays in the buffer until the rest of it arrives.
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop() ?? "";

      for (const block of blocks) {
        const parsed = parseEvent(block);
        if (!parsed) continue;
        if (parsed.event === "progress") {
          try {
            handlers.onProgress(JSON.parse(parsed.data));
            everReceived = true;
          } catch {
            // A snapshot we cannot read is not worth tearing the stream down for; the next one
            // carries the whole state again.
          }
        } else if (parsed.event === "done") {
          stop();
          handlers.onDone();
          return;
        } else if (parsed.event === "error") {
          let retry = false;
          try {
            retry = JSON.parse(parsed.data)?.retry === true;
          } catch {
            retry = false;
          }
          if (!retry) {
            stop();
            handlers.onFailed();
            return;
          }
          // retry: true -- let the connection close and be reopened below.
          return;
        }
        // `timeout` needs no branch: the AI closes after it, and the reconnect below handles it.
      }
    }
  };

  const run = async () => {
    while (!stopped && attempts <= MAX_RECONNECTS) {
      try {
        await connect();
      } catch {
        // Network drop, a deploy, or the connection's own age cap. Reconnecting is free: the first
        // event of a new connection is the full current state, so nothing is missed.
      }
      if (stopped) return;
      attempts += 1;
      if (attempts > MAX_RECONNECTS) break;
      // Reconnecting is only worth it for a batch that has proved it exists. An unknown or expired
      // one would otherwise be retried twenty times in the background for nothing.
      if (!everReceived && attempts >= 2) break;
      await new Promise((resolve) => setTimeout(resolve, RECONNECT_MS));
    }
    if (!stopped) handlers.onFailed();
  };

  void run();
  return stop;
};
